import { useQuery } from "@tanstack/react-query";
import { getDocument } from "@/lib/api";
import {
  getCachedMeta,
  getCachedText,
  setCachedMeta,
  setCachedText,
  mergeDocWithText,
} from "@/lib/documentCache";
import { queryKeys } from "@/lib/cache/queryKeys";
import { staleTimes } from "@/lib/cache/staleTimes";
import { queryClient } from "@/lib/cache/client";

async function fetchDocumentData(id, includeText = false) {
  if (!includeText) {
    const cached = getCachedMeta(id);
    if (cached) return cached;
    const meta = await getDocument(id, { includeText: false });
    setCachedMeta(id, meta);
    const textPayload = getCachedText(id);
    return mergeDocWithText(meta, textPayload);
  }
  const cachedText = getCachedText(id);
  if (cachedText?.text != null) {
    const meta = getCachedMeta(id) || (await getDocument(id, { includeText: false }));
    if (!getCachedMeta(id)) setCachedMeta(id, meta);
    return mergeDocWithText(meta, cachedText);
  }
  const doc = await getDocument(id, { includeText: true });
  setCachedMeta(id, { ...doc, text: undefined });
  setCachedText(id, { text: doc.text, page_offsets: doc.page_offsets });
  return doc;
}

export function fetchDocument(id, { includeText = false } = {}) {
  return queryClient.fetchQuery({
    queryKey: queryKeys.documents.detail(id, { includeText }),
    queryFn: () => fetchDocumentData(id, includeText),
    staleTime: includeText ? staleTimes.documentText : staleTimes.documentDetail,
  });
}

export function useDocument(id, { includeText = false, enabled = true } = {}) {
  return useQuery({
    queryKey: queryKeys.documents.detail(id, { includeText }),
    queryFn: () => fetchDocumentData(id, includeText),
    staleTime: includeText ? staleTimes.documentText : staleTimes.documentDetail,
    enabled: Boolean(id) && enabled,
  });
}
